import type { RenderObject, TableEffectCue } from '@v2/contracts'

interface ActiveEffect { cue: TableEffectCue; start: number; end: number }
/** Disposable, audience-safe timeline driven by the table's controllable clock. */
export class TableEffects {
  private seen = new Set<string>()
  private active: ActiveEffect[] = []

  update(cues: readonly TableEffectCue[], now: number, baseline = false): void {
    if (baseline) { this.clear(); cues.forEach(cue => this.seen.add(cue.id)); return }
    const tails = new Map<string, number>()
    for (const effect of this.active) if (effect.cue.kind === 'label') tails.set(effect.cue.targetId, effect.start + 0.6)
    for (const cue of cues) {
      if (this.seen.has(cue.id)) continue
      this.seen.add(cue.id)
      const start = Math.max(now + Math.min(9000, Math.max(0, cue.delayMs ?? 0)) / 1000,
        cue.kind === 'label' ? tails.get(cue.targetId) ?? now : now)
      if (cue.kind === 'label') tails.set(cue.targetId, start + 0.6)
      this.active.push({ cue, start, end: start + Math.min(5000, Math.max(500, cue.durationMs ?? 2200)) / 1000 })
    }
    // The scene carries a bounded recent history; retain active occurrences too.
    if (this.seen.size > 2048) this.seen = new Set([...cues.map(cue => cue.id), ...this.active.map(effect => effect.cue.id)])
  }
  sample(now: number): readonly (ActiveEffect & { progress: number })[] {
    this.active = this.active.filter(effect => now < effect.end)
    return this.active.filter(effect => now >= effect.start).map(effect => ({ ...effect, progress: (now - effect.start) / (effect.end - effect.start) }))
  }
  rings(now: number, objects: readonly RenderObject[], reducedMotion: boolean): RenderObject[] {
    return this.sample(now).filter(effect => effect.cue.kind === 'ring').flatMap(({ cue, progress }) => {
      const target = objects.find(object => object.id === cue.targetId)
      if (!target) return []
      const radius = Math.max(target.size[0], target.size[2]) * 0.48 + (reducedMotion ? 0.1 : progress * 0.5)
      return Array.from({ length: 32 }, (_, index): RenderObject => ({
        id: `__effect:${cue.id}:${index}`, shape: 'sphere', decorative: true,
        position: [target.position[0] + Math.cos(index * Math.PI / 16) * radius,
          target.position[1] + target.size[1] / 2 + 0.1, target.position[2] + Math.sin(index * Math.PI / 16) * radius],
        size: [0.085, 0.06, 0.085], rotation: 0, color: cue.color ?? '#ffe6a3',
      }))
    })
  }
  clear(): void { this.seen.clear(); this.active = [] }
}
